import { IcInfoWarning } from '@assets/icons/24x24';
import { Box, Button, For, Grid, HStack, Icon, Separator, Text, VStack } from '@chakra-ui/react';
import { useFlightPlansManagementContext } from '@components/cockpit/panels/flightPlan/FlightPlansManagementContext';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import { ButtonWithIcon } from '@components/ui/buttonToggle/ButtonWithIcon';
import { ScrollArea } from '@components/ui/ScrollArea';
import { FormattedMessage } from 'react-intl';

import { DateTimeRange } from '@/types/dateTime.types';
import { ActivationStatusEnum } from '@/types/utm/flightPlan.types';

import FlightPlanStatusIcon from './FlightPlanStatusIcon';

export const StatusFilter = () => {
  const { filters, setFilters } = useFlightPlansManagementContext();

  const handleToggleStatus = (status: ActivationStatusEnum) => {
    const newStatus = new Set(filters.status);
    if (newStatus.has(status)) {
      newStatus.delete(status);
    } else {
      newStatus.add(status);
    }

    setFilters({ status: newStatus });
  };

  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text color="neutral.300">
        <FormattedMessage id="components.flightPlans.filters.statuses" />
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={1}>
        <For each={Object.values(ActivationStatusEnum)}>
          {(status) => {
            const isSelected = filters.status.has(status);
            return (
              <ButtonWithIcon
                key={status}
                label={`flightPlan.status.${status}`}
                icon={<FlightPlanStatusIcon status={status} />}
                data-active={isSelected}
                onClick={() => handleToggleStatus(status)}
              />
            );
          }}
        </For>
      </Grid>
    </VStack>
  );
};

export const ConflictsFilter = () => {
  const { filters, setFilters } = useFlightPlansManagementContext();

  const handleToggleConflict = (conflict: 'with' | 'without') => {
    const newConflict = new Set(filters.conflictStates);
    if (newConflict.has(conflict)) {
      newConflict.delete(conflict);
    } else {
      newConflict.add(conflict);
    }

    setFilters({ conflictStates: newConflict });
  };

  return (
    <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
      <Text color="neutral.300">
        <FormattedMessage id="components.flightPlans.filters.conflicts" />
      </Text>
      <HStack gap={1} width="100%">
        <ButtonWithIcon
          label="global.with"
          flex={1}
          icon={<Icon as={IcInfoWarning} boxSize="24px" color="red.500" data-custom-color />}
          data-active={filters.conflictStates.has('with')}
          onClick={() => handleToggleConflict('with')}
        />
        <ButtonWithIcon
          label="global.without"
          flex={1}
          icon={<Box marginX={1} backgroundColor="neutral.500" width="8px" height="3px" />}
          data-active={filters.conflictStates.has('without')}
          onClick={() => handleToggleConflict('without')}
        />
      </HStack>
    </VStack>
  );
};
export default function FlightPlanFilters() {
  const { filters, setFilters, canReset, resetFilters } = useFlightPlansManagementContext();

  return (
    <VStack gap={0.25} backgroundColor="neutral.black" height="100%" width="400px" flexShrink={0} userSelect="none">
      <HStack
        width="100%"
        height="56px"
        backgroundColor="neutral.850"
        paddingX={2}
        flexShrink={0}
        justifyContent="space-between"
      >
        <Button variant="toggle" size="toggleS" data-active={canReset} disabled={!canReset} onClick={resetFilters}>
          <Text>
            <FormattedMessage id="components.notificationSearch.filters.reset" />
          </Text>
        </Button>
      </HStack>
      <ScrollArea backgroundColor="neutral.850">
        <VStack
          width="100%"
          flex={1}
          backgroundColor="neutral.850"
          gap={0}
          separator={<Separator borderColor="neutral.900" />}
        >
          <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
            <CustomInputDateTimeRange
              range={filters.range}
              singleLine={false}
              showSecond={false}
              onChange={async (range: DateTimeRange) => setFilters({ range })}
            />
          </VStack>
          <StatusFilter />
          <ConflictsFilter />
        </VStack>
      </ScrollArea>
    </VStack>
  );
}
